import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, GoogleMapUnavailable } from "@/lib/mapProvider";

/** Google Maps JS API equivalent of the Leaflet CoordinateMap in
 * PppoeAccountDetail.js — a single draggable marker, click-to-pin. Kept as
 * a separate component (rather than branching inside one) since the two
 * mapping libraries don't share a rendering model. */
export default function GoogleCoordinatePicker({ apiKey, mapCenter, hasCoords, zoom, height, onPick, onDrag }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onPickRef = useRef(onPick);
  const onDragRef = useRef(onDrag);
  const [status, setStatus] = useState("loading");

  onPickRef.current = onPick;
  onDragRef.current = onDrag;

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: { lat: mapCenter[0], lng: mapCenter[1] },
          zoom,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });
        map.addListener("click", (e) => onPickRef.current(e.latLng.lat(), e.latLng.lng()));
        mapRef.current = map;
        setStatus("ready");
      })
      .catch((e) => setStatus(e.message === "NO_KEY" ? "no_key" : "error"));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "ready" || !mapRef.current || !window.google) return;
    const maps = window.google.maps;
    mapRef.current.panTo({ lat: mapCenter[0], lng: mapCenter[1] });
    mapRef.current.setZoom(zoom);
    if (hasCoords) {
      if (!markerRef.current) {
        markerRef.current = new maps.Marker({
          position: { lat: mapCenter[0], lng: mapCenter[1] },
          map: mapRef.current,
          draggable: true,
        });
        markerRef.current.addListener("dragend", (e) => onDragRef.current({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
      } else {
        markerRef.current.setPosition({ lat: mapCenter[0], lng: mapCenter[1] });
      }
    } else if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, mapCenter[0], mapCenter[1], hasCoords, zoom]);

  if (status === "no_key" || status === "error") return <GoogleMapUnavailable height={height} status={status} />;
  return <div ref={containerRef} style={{ height }} className="border border-nhp-border" />;
}
